/**
 * Routes that render for a signed-out visitor. These are exactly the
 * `app-routing.module.ts` entries that do NOT sit behind `AuthenticatedGuard`
 * — `invoice` (payment checkout + receipts, linked from emails we send to
 * families), plus the password/email flows.
 *
 * Why a hand-kept list instead of walking the route config for `canActivate`:
 * when a 401 lands mid-boot the target route has not been recognised yet, so
 * there is no `ActivatedRouteSnapshot` to inspect — only a URL string. The
 * first segment is all that is available and all that is needed.
 */
const PUBLIC_ROUTE_SEGMENTS = ['invoice', 'forgotten-password', 'reset-password', 'confirm-email'];

/**
 * Whether `url` is a page a signed-out visitor is allowed to sit on, so a 401
 * must clear auth state WITHOUT bouncing them to Login.
 *
 * Matching is on the first path segment only, compared whole — a prefix test
 * would treat `/invoice-admin` as public, and comparing the full URL would
 * miss `/invoice/<uuid>/receipts/1`. Query string and hash fragment are
 * stripped first: the router serialises those onto `url`, and `reset-password`
 * carries its token as a path segment.
 */
export function isPublicRoute(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }

  const path = url.split('?')[0].split('#')[0];
  const segment = path.split('/').filter((part) => !!part)[0];

  return !!segment && PUBLIC_ROUTE_SEGMENTS.indexOf(segment) !== -1;
}
